import { BASE_URL } from '@avanta/shared/constants/common';
import { service, type ServiceRequestConfig } from '@avanta/shared/services/api';
import {
  type AdminRole,
  type Enterprise,
  type UpdateAdminEnterprisePayload,
} from '@avanta/shared/types/enterprises';
import { type AxiosResponse } from 'axios';
import { ENTERPRISE_CONTRACT_STATUS } from 'constants/enterprise';
import { type Employee } from 'types/employees';
import {
  type AdminEnterprise,
  type CreateAdminEnterprisePayload,
  type CountAdminsByEnterprise,
  type CountEmployeesByEnterprise,
  type EnterpriseActivityItem,
  type EnterpriseContract,
  type EnterpriseInvoicesResponse,
  type UpdateAdminPreferencesPayload,
  type GetEmployeesByEnterpriseParams,
  type GetEnterpriseActivityParams,
  type GetEnterpriseInvoicesParams,
  type GetEnterpriseInvoicePdfParams,
  type GetAdminsByEnterpriseParams,
  type GetEnterpriseContractsParams,
} from 'types/enterprises';

const ENTERPRISES_URL = `${BASE_URL}enterprises`;

type EmployeesCountParams = Pick<
  GetEmployeesByEnterpriseParams,
  'enterpriseId' | 'search' | 'hasUpcomingSession' | 'hasActiveRecord' | 'sessionStatus'
>;

interface EnterprisesServiceTypes {
  getEmployeesByEnterprise: (
    params: GetEmployeesByEnterpriseParams
  ) => Promise<AxiosResponse<Employee[]>>;
  getCountEmployeesByEnterprise: (
    params: EmployeesCountParams
  ) => Promise<AxiosResponse<CountEmployeesByEnterprise>>;
  getAdminsByEnterprise: (
    enterpriseId: string,
    params?: GetAdminsByEnterpriseParams
  ) => Promise<AxiosResponse<AdminEnterprise[]>>;
  getCountAdminsByEnterprise: (
    enterpriseId: string,
    role?: AdminRole
  ) => Promise<AxiosResponse<CountAdminsByEnterprise>>;
  createAdmin: (payload: CreateAdminEnterprisePayload) => Promise<AxiosResponse<AdminEnterprise>>;
  editAdmin: (
    adminId: string,
    payload: UpdateAdminEnterprisePayload
  ) => Promise<AxiosResponse<AdminEnterprise>>;
  updateAdminPreferences: (
    enterpriseId: string,
    adminId: string,
    payload: UpdateAdminPreferencesPayload
  ) => Promise<AxiosResponse<AdminEnterprise>>;
  updateEnterprise: (
    enterpriseId: string,
    payload: Enterprise
  ) => Promise<AxiosResponse<Enterprise>>;
  getInvoices: (
    insuranceId: string,
    params?: GetEnterpriseInvoicesParams
  ) => Promise<AxiosResponse<EnterpriseInvoicesResponse>>;
  getInvoicePdf: (
    insuranceId: string,
    params: GetEnterpriseInvoicePdfParams
  ) => Promise<AxiosResponse<Blob>>;
  getEnterpriseContracts: (
    enterpriseId: string,
    params?: GetEnterpriseContractsParams
  ) => Promise<AxiosResponse<EnterpriseContract[]>>;
  getActivity: (
    enterpriseId: string,
    params?: GetEnterpriseActivityParams
  ) => Promise<AxiosResponse<EnterpriseActivityItem[]>>;
}

export const EnterprisesService: EnterprisesServiceTypes = {
  getEmployeesByEnterprise: async ({
    enterpriseId,
    offset = 0,
    limit = 50,
    search,
    hasUpcomingSession,
    hasActiveRecord,
    sessionStatus,
    sortBy,
    sortOrder,
  }) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/employees`, {
      params: {
        offset,
        limit,
        ...(search ? { search } : {}),
        hasUpcomingSession,
        hasActiveRecord,
        sessionStatus,
        sortBy,
        sortOrder,
      },
    }),
  getCountEmployeesByEnterprise: async ({
    enterpriseId,
    search,
    hasUpcomingSession,
    hasActiveRecord,
    sessionStatus,
  }) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/employees/count`, {
      params: {
        ...(search ? { search } : {}),
        hasUpcomingSession,
        hasActiveRecord,
        sessionStatus,
      },
    }),
  getAdminsByEnterprise: async (enterpriseId, params) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/admins`, {
      params: {
        offset: params?.offset,
        limit: params?.limit,
        search: params?.search,
        sortBy: params?.sortBy,
        sortOrder: params?.sortOrder,
        ...(params?.role ? { role: params.role } : {}),
      },
    }),
  getCountAdminsByEnterprise: async (enterpriseId, role) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/admins/count`, {
      params: {
        ...(role ? { role } : {}),
      },
    }),
  createAdmin: async payload =>
    service.post(`${ENTERPRISES_URL}/${payload.enterpriseId}/admins`, payload),
  editAdmin: async (adminId, payload) =>
    service.put(`${ENTERPRISES_URL}/${payload.enterpriseId}/admins/${adminId}/role`, payload),
  updateAdminPreferences: async (enterpriseId, adminId, payload) =>
    service.put(`${ENTERPRISES_URL}/${enterpriseId}/admins/${adminId}/preferences`, payload),
  updateEnterprise: async (enterpriseId, payload) =>
    service.put(`${ENTERPRISES_URL}/${enterpriseId}`, payload),
  // TODO: Odoo only filters by NIF and date range; search/offset/limit are in-memory.
  getInvoices: async (insuranceId, params) =>
    service.get(`${ENTERPRISES_URL}/${insuranceId}/invoices`, { params }),
  getInvoicePdf: async (insuranceId, { invoiceId, forceDownload = true }) => {
    const config: ServiceRequestConfig = {
      params: { forceDownload },
      responseType: 'blob',
      skipGlobalErrorHandler: true,
    };
    return service.get(`${ENTERPRISES_URL}/${insuranceId}/invoices/${invoiceId}/pdf`, config);
  },
  getEnterpriseContracts: async (enterpriseId, params) =>
    service.get(`${ENTERPRISES_URL}/insurances/${enterpriseId}/contracts`, {
      params: {
        status: params?.status ?? ENTERPRISE_CONTRACT_STATUS.ACTIVE,
        patientId: params?.patientId,
      },
    }),
  getActivity: async (enterpriseId, params) =>
    service.get(`${ENTERPRISES_URL}/${enterpriseId}/activity`, {
      params: {
        limit: params?.limit,
        scope: params?.scope,
        types: params?.types?.length ? params.types.join(',') : undefined,
      },
    }),
};
